'use client';

import { useEffect } from 'react';
import { initBotId } from 'botid/client/core';
import { BOTID_PROTECTED_ROUTES } from '@/lib/botid-routes';

declare global {
  interface Window {
    /** BotID has been started on this page load (see bootBotIdOnce). */
    __iaBotId?: boolean;
  }
}

/**
 * Start BotID exactly once per page load.
 *
 * Why not the package's <BotIdClient> in <head>: that renders an inline
 * <script>. On a server-rendered page the browser runs it, but on a shell
 * React renders on the client (the real 404 from notFound() in
 * generateMetadata, both error.tsx boundaries) React inserts the element and
 * browsers never execute a script inserted that way. The challenge never
 * loaded there, every /api/product-search POST left without x-is-human, and
 * checkBotId refused each one as a bot: the 404 recovery search, and its
 * Retry, could not work for anyone (2026-10-13).
 *
 * Why once, and never both paths: a second initBotId on the same page builds
 * a second challenge holder that waits for a challenge the first one already
 * took, and every protected request then hangs on it. The flag lives on
 * window so StrictMode's double effect and HMR cannot start it twice.
 */
export function bootBotIdOnce(win: { __iaBotId?: boolean }, init: () => void): boolean {
  if (win.__iaBotId) return false;
  win.__iaBotId = true;
  init();
  return true;
}

/** Mounted first in the root <body>, so its effect runs before any child's. */
export function BotIdBoot() {
  useEffect(() => {
    bootBotIdOnce(window, () => initBotId({ protect: [...BOTID_PROTECTED_ROUTES] }));
  }, []);
  return null;
}
